import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useToast } from '../../../components/Toast';
import { getImageUrl } from '../../../utils/mediaUtils';
import { 
  BookOpen, 
  Upload, 
  Clock, 
  Plus, 
  Trash2, 
  Save, 
  Tag, 
  Calendar, 
  Eye, 
  X, 
  FileText, 
  Edit3, 
  User, 
  Sparkles, 
  CheckCircle2, 
  ListPlus,
  Layers,
  Image as ImageIcon
} from 'lucide-react';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const DEFAULT_CATEGORIES = [
  'Education Trends',
  'Student Highlights',
  'Teacher Insights'
];

const FEATURE_ICON_OPTIONS = [
  { value: 'fa-star', label: 'Bintang (Star / Sorotan)' },
  { value: 'fa-trophy', label: 'Piala (Trophy / Prestasi)' },
  { value: 'fa-award', label: 'Medali (Award / Penghargaan)' },
  { value: 'fa-book-open', label: 'Buku (Education / Belajar)' },
  { value: 'fa-lightbulb', label: 'Lampu (Inovasi / Ide)' },
  { value: 'fa-users', label: 'Komunitas (Team / Murid)' },
  { value: 'fa-heart', label: 'Hati (Karakter / Kepedulian)' },
  { value: 'fa-globe', label: 'Globe (Wawasan Global)' },
  { value: 'fa-university', label: 'Gedung (Fasilitas / Akademik)' },
  { value: 'fa-handshake', label: 'Jabat Tangan (Kemitraan)' }
];

export default function NewsArticlesForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const getAuthHeaders = () => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  const [formData, setFormData] = useState({
    kicker: 'Blog / Articles',
    title: 'Education Trends, Student Highlights & Insights',
    description: 'Thoughtful reads that reflect learning progress, community voices, classroom experiences, and newsletters from our school community.',
    items: [
      {
        id: 'education-trends',
        title: 'Education Trends',
        subtitle: 'Emerging Pedagogical Frameworks & Tech Integration',
        category: 'Education Trends',
        tag: 'Education Trends',
        author: 'Editorial Team',
        description: 'Insights on emerging learning approaches, curriculum updates, and global education trends.',
        content: 'Our comprehensive exploration of emerging educational trends shows how modern pedagogical frameworks and interactive technology are transforming student engagement. Educators are adapting blended learning tools to cultivate critical thinking, adaptability, and independent research skills for 21st-century learners.',
        highlights: [
          'Adoption of student-centric interactive classrooms',
          'Curated digital research projects across junior and senior levels',
          'Integration of AI literacy and critical media evaluation'
        ],
        image: '/assets/news/news3.jpg',
        date: 'October 2026',
        link: 'news-blog-education-trends'
      },
      {
        id: 'student-highlights',
        title: 'Student Highlights',
        subtitle: 'Celebrating Student Ingenuity, Leadership & Arts',
        category: 'Student Highlights',
        tag: 'Student Highlights',
        author: 'Student Council & Faculty',
        description: 'Stories of student achievements, creativity, leadership, and school life moments.',
        content: 'From science fairs to creative arts showcases, our students continue to excel and demonstrate resilience, teamwork, and leadership. This edition highlights outstanding achievements in academic competitions and impactful community outreach initiatives organized by the student council.',
        highlights: [
          'First place award in regional STEM robotics competition',
          'Over 40 student visual art submissions exhibited on campus',
          'Student-led environmental sustainability campaigns'
        ],
        image: '/assets/news/news4.jpg',
        date: 'September 2026',
        link: 'news-blog-student-highlights'
      },
      {
        id: 'teacher-insights',
        title: 'Teacher Insights',
        subtitle: 'Actionable Classroom Perspectives from Senior Educators',
        category: 'Teacher Insights',
        tag: 'Teacher Insights',
        author: 'Faculty Mentorship Circle',
        description: 'Perspective pieces from our educators about classroom experiences and learning journeys.',
        content: 'Reflections from our experienced educators highlight the importance of emotional wellbeing and inquiry-based learning in the secondary curriculum. In this piece, our faculty shares actionable strategies for fostering curiosity and meaningful student-teacher collaboration.',
        highlights: [
          'Implementation of weekly social-emotional check-in circles',
          'Collaborative lesson development between sciences and humanities',
          'Continuous professional workshops on adaptive teaching'
        ],
        image: '/assets/news/news7.jpg',
        date: 'August 2026',
        link: 'news-blog-teacher-insights'
      }
    ]
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false
  });
  const [pendingItems, setPendingItems] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const [brokenImages, setBrokenImages] = useState({});
  const [activeCategoryFilter, setActiveCategoryFilter] = useState('ALL');

  // Article Editor Modal State
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null); // null means adding a new article
  const [editingArticle, setEditingArticle] = useState(null);
  const [slideUploading, setSlideUploading] = useState({ 0: false, 1: false, 2: false });
  const [slideUploadProgress, setSlideUploadProgress] = useState({ 0: 0, 1: 0, 2: 0 });
  const [newHighlightText, setNewHighlightText] = useState('');
  const [newFeatureTitle, setNewFeatureTitle] = useState('');
  const [newFeatureDetail, setNewFeatureDetail] = useState('');
  const [newFeatureIcon, setNewFeatureIcon] = useState('fa-star');
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [customCategoryInput, setCustomCategoryInput] = useState('');

  useEffect(() => {
    const init = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const res = await fetch(`${apiBase}/homepage/cms/media?_t=${Date.now()}`, {
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      if (!res.ok) return;
      const json = await res.json();
      if (json.ok && Array.isArray(json.data)) {
        const map = {};
        json.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            const numId = Number(mediaId);
            if (!isNaN(numId)) map[numId] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.warn('[NewsArticlesForm] Media fetch error:', err.message);
    }
  };

  const resolveImage = (mediaId, index = 0) => {
    if (!mediaId || brokenImages[index]) return '';
    if (mediaMap && (mediaMap[mediaId] || mediaMap[String(mediaId)])) {
      const mapped = mediaMap[mediaId] || mediaMap[String(mediaId)];
      if (mapped.startsWith('http://') || mapped.startsWith('https://')) return mapped;
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${mapped}` : mapped;
    }
    const resolved = getImageUrl(mediaId, mediaMap);
    if (resolved) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://')) return resolved;
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${resolved}` : resolved;
    }
    return '';
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=news-updates`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => 
          s.section_type === 'news-articles' || 
          s.section_type === 'news-blog-articles' || 
          s.section_type === 'news-list' ||
          s.section_type === 'articles'
        );
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=news-updates`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => 
            s.section_type === 'news-articles' || 
            s.section_type === 'news-blog-articles' || 
            s.section_type === 'news-list' ||
            s.section_type === 'articles'
          );
        }
      }

      // If section is still not found, auto-ensure it exists via create-or-get endpoint
      if (!foundSection) {
        try {
          const createRes = await fetch(`${apiBase}/homepage/cms/sections/create-or-get`, {
            method: 'POST',
            credentials: 'include',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              page: 'news-updates',
              section_type: 'news-articles',
              content: {
                en: {
                  kicker: 'Latest Insights & Highlights',
                  title: 'Education Trends, Student Highlights & Insights',
                  description: 'Thoughtful reads that reflect learning progress, community voices, classroom experiences, and newsletters from our school community.',
                  items: formData.items
                }
              }
            })
          });
          if (createRes.ok) {
            const createJson = await createRes.json();
            foundSection = createJson.data;
          }
        } catch (ce) {
          console.warn('[NewsArticlesForm] Auto-create section error:', ce);
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);

        let content = foundSection.content_published;
        if (!content || content === '{}' || content === '[]') {
          content = foundSection.content;
        }
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = {};
          }
        }

        const en = content?.en || content || {};
        const rawItems = Array.isArray(en.items) && en.items.length > 0 
          ? en.items 
          : (Array.isArray(en.blog_articles) && en.blog_articles.length > 0 ? en.blog_articles : formData.items);

        const loadedItems = rawItems.map((item, idx) => ({
          id: item.id || `article-${idx + 1}`,
          title: item.title || '',
          subtitle: item.subtitle || '',
          category: item.category || item.tag || 'Education Trends',
          tag: item.tag || item.category || 'Education Trends',
          author: item.author || 'Editorial Team',
          description: item.description || item.summary || '',
          content: item.content || item.body || item.description || '',
          highlights: Array.isArray(item.highlights) ? item.highlights : (typeof item.highlights === 'string' ? [item.highlights] : []),
          features: Array.isArray(item.features) ? item.features : [],
          image: item.image || (Array.isArray(item.images) ? item.images[0] : '') || '',
          images: Array.isArray(item.images) && item.images.length > 0 
            ? item.images 
            : (item.image ? [item.image] : []),
          date: item.date || '',
          link: item.link || `news-blog-${item.id || idx + 1}`
        }));

        const loadedData = {
          kicker: en.kicker || 'Blog / Articles',
          title: en.title || 'Education Trends, Student Highlights & Insights',
          description: en.description || 'Thoughtful reads that reflect learning progress, community voices, classroom experiences, and newsletters from our school community.',
          items: loadedItems
        };

        const pFields = { kicker: false, title: false, description: false };
        const pItems = {};

        try {
          const pendingRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendingRes.ok) {
            const pData = await pendingRes.json();
            if (pData.success && pData.data?.pending_changes) {
              pData.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  let val = c.new_value;
                  if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
                    try { val = JSON.parse(val); } catch (e) {}
                  }

                  if (['kicker', 'title', 'description'].includes(c.field_id)) {
                    pFields[c.field_id] = true;
                    if (val !== undefined && val !== null) {
                      loadedData[c.field_id] = val;
                    }
                  } else if (c.field_id?.includes('items.')) {
                    const m = c.field_id.match(/items\.(\d+)(?:\.(add|update|delete))?/);
                    if (m) {
                      const itemIdx = parseInt(m[1], 10);
                      const op = m[2] || 'update';
                      pItems[`item_${itemIdx}`] = true;
                      if (op === 'delete') {
                        pItems[`item_${itemIdx}_delete`] = true;
                      } else if (val && typeof val === 'object') {
                        if (loadedItems[itemIdx]) {
                          loadedItems[itemIdx] = { ...loadedItems[itemIdx], ...val };
                        } else {
                          loadedItems.push(val);
                        }
                      }
                    }
                  }
                }
              });
            }
          }
        } catch (pe) {
          console.warn('[NewsArticlesForm] Pending check error:', pe);
        }

        setFormData(loadedData);
        setOriginalData(loadedData);
        setPendingFields(pFields);
        setPendingItems(pItems);
      }
    } catch (err) {
      console.error('[NewsArticlesForm] Load error:', err);
      toast.error('Failed to load Blog & Articles content');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const nextItems = [...prev.items];
      nextItems[index] = { ...nextItems[index], [field]: value };
      return { ...prev, items: nextItems };
    });
  };

  const getCategoryColor = (cat) => {
    const c = (cat || '').toLowerCase();
    if (c.includes('trend')) return { bg: '#fef3c7', text: '#92400e', border: '#fde68a' };
    if (c.includes('highlight') || c.includes('student')) return { bg: '#e0e7ff', text: '#3730a3', border: '#c7d2fe' };
    if (c.includes('teacher') || c.includes('insight')) return { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' };
    return { bg: '#f1f5f9', text: '#475569', border: '#e2e8f0' };
  };

  // Open Modal for adding new article
  const handleOpenAddModal = () => {
    setEditingIndex(null);
    setIsCustomCategory(false);
    setCustomCategoryInput('');
    setNewHighlightText('');
    setNewFeatureTitle('');
    setNewFeatureDetail('');
    setNewFeatureIcon('fa-star');
    setSlideUploading({ 0: false, 1: false, 2: false });
    setSlideUploadProgress({ 0: 0, 1: 0, 2: 0 });
    setEditingArticle({
      id: `article-${Date.now()}`,
      title: '',
      subtitle: '',
      category: 'Education Trends',
      tag: 'Education Trends',
      date: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      author: 'Editorial Team',
      description: '',
      content: '',
      highlights: [],
      features: [],
      image: '',
      images: ['', '', ''],
      link: ''
    });
    setEditorModalOpen(true);
  };

  // Open Modal for editing existing article
  const handleOpenEditModal = (index) => {
    setEditingIndex(index);
    const item = formData.items[index];
    const isCustom = !DEFAULT_CATEGORIES.includes(item.category) && Boolean(item.category);
    setIsCustomCategory(isCustom);
    setCustomCategoryInput(isCustom ? item.category : '');
    setNewHighlightText('');
    setNewFeatureTitle('');
    setNewFeatureDetail('');
    setNewFeatureIcon('fa-star');
    setSlideUploading({ 0: false, 1: false, 2: false });
    setSlideUploadProgress({ 0: 0, 1: 0, 2: 0 });

    const existingImages = Array.isArray(item.images) && item.images.length > 0 
      ? item.images 
      : (item.image ? [item.image] : []);
    const images3 = [
      resolveImage(existingImages[0]) || existingImages[0] || item.image || '',
      resolveImage(existingImages[1]) || existingImages[1] || '',
      resolveImage(existingImages[2]) || existingImages[2] || ''
    ];

    setEditingArticle({
      ...item,
      highlights: Array.isArray(item.highlights) ? [...item.highlights] : [],
      features: Array.isArray(item.features) ? [...item.features] : [],
      images: images3,
      image: images3[0] || item.image || ''
    });
    setEditorModalOpen(true);
  };

  // Upload image for specific slide (0, 1, or 2) in modal with real-time percentage
  const handleModalSlideImageUpload = (slideIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Format gambar harus JPG, PNG, atau WebP');
      return;
    }

    setSlideUploading(prev => ({ ...prev, [slideIndex]: true }));
    setSlideUploadProgress(prev => ({ ...prev, [slideIndex]: 0 }));

    const uploadData = new FormData();
    uploadData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/media/upload`);
    xhr.withCredentials = true;

    const token = localStorage.getItem('token') || 
                  localStorage.getItem('ph_auth_token') || 
                  localStorage.getItem('auth_token') || 
                  sessionStorage.getItem('token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded / ev.total) * 100);
        setSlideUploadProgress(prev => ({ ...prev, [slideIndex]: pct }));
      }
    };

    xhr.onload = () => {
      setSlideUploading(prev => ({ ...prev, [slideIndex]: false }));
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const res = JSON.parse(xhr.responseText);
          const data = res.data || res;
          const mediaId = data.media_id || data.id || res.media_id || res.id;
          let mediaUrl = data.url || data.file_path || data.path || res.url;
          if (mediaUrl && !mediaUrl.startsWith('http://') && !mediaUrl.startsWith('https://')) {
            const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
            mediaUrl = `${origin}${mediaUrl.startsWith('/') ? '' : '/'}${mediaUrl}`;
          }
          const finalImageValue = mediaUrl || (mediaId ? String(mediaId) : '');

          if (finalImageValue) {
            setEditingArticle(prev => {
              const curImages = Array.isArray(prev.images) ? [...prev.images] : ['', '', ''];
              curImages[slideIndex] = finalImageValue;
              return {
                ...prev,
                images: curImages,
                image: slideIndex === 0 ? finalImageValue : (prev.image || finalImageValue)
              };
            });

            if (mediaUrl && mediaId) {
              setMediaMap(prev => ({
                ...prev,
                [mediaId]: mediaUrl,
                [String(mediaId)]: mediaUrl
              }));
            }
            toast.success(`Foto Slide ${slideIndex + 1} berhasil diunggah`);
          } else {
            toast.error('Gagal membaca ID media');
          }
        } catch (err) {
          toast.error('Gagal memproses respon upload');
        }
      } else {
        toast.error(`Upload gagal (${xhr.status})`);
      }
    };

    xhr.onerror = () => {
      setSlideUploading(prev => ({ ...prev, [slideIndex]: false }));
      toast.error('Terjadi gangguan jaringan saat upload');
    };

    xhr.send(uploadData);
  };

  const handleRemoveSlideImage = (slideIndex) => {
    setEditingArticle(prev => {
      const curImages = Array.isArray(prev.images) ? [...prev.images] : ['', '', ''];
      curImages[slideIndex] = '';
      const firstAvailable = curImages.find(Boolean) || '';
      return {
        ...prev,
        images: curImages,
        image: slideIndex === 0 ? firstAvailable : prev.image
      };
    });
    toast.info(`Foto Slide ${slideIndex + 1} dihapus`);
  };

  // Add bullet point highlight in modal
  const handleAddHighlight = () => {
    if (!newHighlightText.trim()) return;
    setEditingArticle(prev => ({
      ...prev,
      highlights: [...(prev.highlights || []), newHighlightText.trim()]
    }));
    setNewHighlightText('');
  };

  const handleRemoveHighlight = (hIdx) => {
    setEditingArticle(prev => ({
      ...prev,
      highlights: (prev.highlights || []).filter((_, idx) => idx !== hIdx)
    }));
  };

  // Add structured Key Pillar feature in modal (Optional)
  const handleAddFeature = () => {
    if (!newFeatureTitle.trim()) {
      toast.warning('Silakan isi judul pilar terlebih dahulu');
      return;
    }
    const newFeature = {
      icon: newFeatureIcon || 'fa-star',
      title: newFeatureTitle.trim(),
      detail: (newFeatureDetail || '').trim()
    };
    setEditingArticle(prev => ({
      ...prev,
      features: [...(prev.features || []), newFeature]
    }));
    setNewFeatureTitle('');
    setNewFeatureDetail('');
    toast.success(`Pilar "${newFeature.title}" berhasil dimasukkan ke daftar`);
  };

  const handleEditFeature = (featIdx) => {
    const feat = (editingArticle.features || [])[featIdx];
    if (!feat) return;
    setNewFeatureIcon(feat.icon || 'fa-star');
    setNewFeatureTitle(feat.title || '');
    setNewFeatureDetail(feat.detail || '');
    setEditingArticle(prev => ({
      ...prev,
      features: (prev.features || []).filter((_, idx) => idx !== featIdx)
    }));
    toast.info('Pilar dimuat kembali ke kolom input di bawah untuk disunting');
  };

  const handleRemoveFeature = (featIdx) => {
    setEditingArticle(prev => ({
      ...prev,
      features: (prev.features || []).filter((_, idx) => idx !== featIdx)
    }));
    toast.info('Pilar dihapus dari daftar');
  };

  // Save changes from modal into formData state
  const handleSaveModalChanges = () => {
    if (!editingArticle?.title?.trim()) {
      toast.warning('Silakan isi judul artikel');
      return false;
    }

    const cleanedImages = (Array.isArray(editingArticle.images) 
      ? editingArticle.images 
      : (editingArticle.image ? [editingArticle.image] : [])
    ).filter(Boolean).map(img => {
      const resolved = resolveImage(img) || (mediaMap && (mediaMap[img] || mediaMap[String(img)])) || img;
      if (typeof resolved === 'string' && (resolved.startsWith('http://') || resolved.startsWith('https://'))) {
        return resolved;
      }
      if (typeof resolved === 'string' && resolved.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
        return `${origin}${resolved}`;
      }
      return resolved;
    });

    if (cleanedImages.length === 0 && !editingArticle?.image) {
      toast.warning('Silakan upload minimal foto sampul (Slide 1) terlebih dahulu');
      return false;
    }

    if (!editingArticle?.description?.trim()) {
      toast.warning('Silakan isi ringkasan singkat (excerpt) artikel');
      return false;
    }

    if (!editingArticle?.content?.trim()) {
      toast.warning('Silakan isi berita lengkap artikel');
      return false;
    }

    const finalCategory = isCustomCategory 
      ? (customCategoryInput.trim() || 'Education Trends')
      : (editingArticle.category || 'Education Trends');

    // Auto-commit pending highlight if user typed but did not press 'Tambah Poin'
    const finalHighlights = [...(editingArticle.highlights || [])];
    if (newHighlightText && newHighlightText.trim()) {
      finalHighlights.push(newHighlightText.trim());
    }

    // Auto-commit pending feature pillar if user typed but did not press 'Tambahkan ke Daftar Pilar'
    const finalFeatures = [...(editingArticle.features || [])];
    if (newFeatureTitle && newFeatureTitle.trim()) {
      finalFeatures.push({
        icon: newFeatureIcon || 'fa-star',
        title: newFeatureTitle.trim(),
        detail: (newFeatureDetail || '').trim()
      });
    }

    const updatedItem = {
      ...editingArticle,
      title: editingArticle.title.trim(),
      subtitle: editingArticle.subtitle?.trim() || '',
      category: finalCategory,
      tag: finalCategory,
      author: editingArticle.author?.trim() || 'Editorial Team',
      date: editingArticle.date?.trim() || new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      description: editingArticle.description?.trim() || '',
      content: editingArticle.content?.trim() || '',
      highlights: finalHighlights.filter(Boolean),
      features: finalFeatures.filter(f => f && (f.title || f.detail)),
      image: cleanedImages[0] || editingArticle.image || '',
      images: cleanedImages,
      link: `news-blog-${editingArticle.id || editingArticle.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
    };

    let targetIdx = editingIndex;
    if (editingIndex === null) {
      // Prepend new item
      setFormData(prev => ({
        ...prev,
        items: [updatedItem, ...prev.items]
      }));
      targetIdx = 0;
      toast.success(`Artikel "${updatedItem.title}" berhasil ditambahkan ke daftar`);
    } else {
      setFormData(prev => {
        const next = [...prev.items];
        next[editingIndex] = updatedItem;
        return { ...prev, items: next };
      });
      toast.success(`Perubahan artikel "${updatedItem.title}" tersimpan di draf`);
    }

    setNewHighlightText('');
    setNewFeatureTitle('');
    setNewFeatureDetail('');
    setEditorModalOpen(false);
    return { item: updatedItem, index: targetIdx };
  };

  // Save and directly submit to approval workflow
  const handleSaveAndSubmitModal = async () => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const saved = handleSaveModalChanges();
    if (!saved) return;

    const { item, index } = saved;
    const isNew = editingIndex === null;
    const key = `item_${index}`;

    setSubmitting(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${index}.${isNew ? 'add' : 'update'}`,
          field_name: isNew ? `Add Article: ${item.title}` : `Article: ${item.title}`,
          old_value: isNew ? null : (originalData.items?.[index] || {}),
          new_value: item,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Artikel "${item.title}" berhasil diajukan untuk approval Kepala Sekolah!`);
        setPendingItems(prev => ({ ...prev, [key]: true }));
        setOriginalData(prev => {
          const next = [...(prev.items || [])];
          if (isNew) {
            next.unshift(item);
          } else {
            next[index] = item;
          }
          return { ...prev, items: next };
        });
        try {
          sessionStorage.removeItem('cms_page_news-updates');
          sessionStorage.removeItem('cms_page_news-and-updates');
          sessionStorage.removeItem('cms_sections_news-updates');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[NewsArticlesForm] Submit modal error:', err);
      toast.error('Gagal mengajukan artikel: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  // Submit existing card from list
  const handleSubmitItem = async (index) => {
    if (!sectionId) {
      toast.error('Section ID not found');
      return;
    }

    const item = formData.items[index];
    if (!item?.title) {
      toast.warning('Silakan isi judul artikel');
      return;
    }
    if (!item?.image || brokenImages[index]) {
      toast.warning('Silakan upload foto artikel terlebih dahulu sebelum submit');
      return;
    }

    const isNew = !originalData.items?.[index] || !originalData.items[index]?.id;
    const op = isNew ? 'add' : 'update';
    const key = `item_${index}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    const autoLink = item.link || `news-blog-${item.id || item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    const payloadItem = {
      ...item,
      link: autoLink
    };

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${index}.${op}`,
          field_name: isNew ? `Add Article: ${item.title}` : `Article: ${item.title}`,
          old_value: isNew ? null : (originalData.items?.[index] || {}),
          new_value: payloadItem,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(isNew 
          ? `Pengajuan artikel baru "${item.title}" berhasil disubmit untuk approval`
          : `Artikel "${item.title}" berhasil disubmit untuk approval`);
        setPendingItems(prev => ({ ...prev, [key]: true }));
        setOriginalData(prev => {
          const next = [...(prev.items || [])];
          next[index] = payloadItem;
          return { ...prev, items: next };
        });
        try {
          sessionStorage.removeItem('cms_page_news-updates');
          sessionStorage.removeItem('cms_page_news-and-updates');
          sessionStorage.removeItem('cms_sections_news-updates');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[NewsArticlesForm] Item submit error:', err);
      toast.error('Error submitting article: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  // Remove article
  const handleRemoveItem = async (index) => {
    if (formData.items.length <= 1) {
      toast.warning('Setidaknya harus ada satu artikel berita');
      return;
    }

    const item = formData.items[index];
    const origIndex = originalData.items?.findIndex(it => 
      (it.id && item.id && String(it.id) === String(item.id)) ||
      (it.title && item.title && it.title.trim().toLowerCase() === item.title.trim().toLowerCase())
    );
    const isExistingInDb = origIndex !== -1 && origIndex !== undefined;
    const targetIndex = isExistingInDb ? origIndex : index;

    if (!isExistingInDb || !sectionId) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
      return;
    }

    const confirmDelete = window.confirm(`Ajukan penghapusan artikel "${item.title || `Item #${index + 1}`}" untuk persetujuan Kepala Sekolah?`);
    if (!confirmDelete) return;

    const key = `item_${index}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${targetIndex}.delete`,
          field_name: `Delete Article: ${item.title || `Item #${targetIndex + 1}`}`,
          old_value: originalData.items[targetIndex],
          new_value: null,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Pengajuan penghapusan "${item.title || `Item #${index + 1}`}" berhasil dikirim untuk approval`);
        setPendingItems(prev => ({
          ...prev,
          [key]: true,
          [`item_${targetIndex}`]: true,
          [`item_${targetIndex}_delete`]: true
        }));
        try {
          sessionStorage.removeItem('cms_page_news-updates');
          sessionStorage.removeItem('cms_page_news-and-updates');
          sessionStorage.removeItem('cms_sections_news-updates');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Gagal mengajukan penghapusan');
      }
    } catch (err) {
      toast.error('Terjadi kesalahan: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  // Quick photo upload from card
  const handleCardImageUpload = (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('File size must be less than 5MB');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, and WebP images are supported');
      return;
    }

    setImageUploading(prev => ({ ...prev, [index]: true }));
    setUploadProgress(prev => ({ ...prev, [index]: 0 }));

    const uploadData = new FormData();
    uploadData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/media/upload`);
    xhr.withCredentials = true;

    const token = localStorage.getItem('token') || 
                  localStorage.getItem('ph_auth_token') || 
                  localStorage.getItem('auth_token') || 
                  sessionStorage.getItem('token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded / ev.total) * 100);
        setUploadProgress(prev => ({ ...prev, [index]: pct }));
      }
    };

    xhr.onload = () => {
      setImageUploading(prev => ({ ...prev, [index]: false }));
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const res = JSON.parse(xhr.responseText);
          const data = res.data || res;
          const mediaId = data.media_id || data.id || res.media_id || res.id;
          let mediaUrl = data.url || data.file_path || data.path || res.url;
          if (mediaUrl && !mediaUrl.startsWith('http://') && !mediaUrl.startsWith('https://')) {
            const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
            mediaUrl = `${origin}${mediaUrl.startsWith('/') ? '' : '/'}${mediaUrl}`;
          }
          const finalVal = mediaUrl || (mediaId ? String(mediaId) : '');

          if (finalVal) {
            handleItemChange(index, 'image', finalVal);
            setFormData(prev => {
              const next = [...prev.items];
              const curImgs = Array.isArray(next[index]?.images) ? [...next[index].images] : ['', '', ''];
              curImgs[0] = finalVal;
              next[index] = { ...next[index], image: finalVal, images: curImgs };
              return { ...prev, items: next };
            });
            setBrokenImages(prev => ({ ...prev, [index]: false }));
            if (mediaUrl && mediaId) {
              setMediaMap(prev => ({
                ...prev,
                [mediaId]: mediaUrl,
                [String(mediaId)]: mediaUrl
              }));
            }
            toast.success('Foto artikel berhasil diupload');
          } else {
            toast.error('Gagal membaca media ID dari server');
          }
        } catch (err) {
          toast.error('Gagal memproses respon upload');
        }
      } else {
        toast.error(`Upload gagal (${xhr.status})`);
      }
    };

    xhr.onerror = () => {
      setImageUploading(prev => ({ ...prev, [index]: false }));
      toast.error('Terjadi gangguan jaringan saat upload');
    };

    xhr.send(uploadData);
  };

  const handleSubmitField = async (field) => {
    if (!sectionId) {
      toast.error('Section ID not found');
      return;
    }

    if (formData[field] === originalData[field]) {
      toast.warning('No changes detected for this field');
      return;
    }

    setSubmitting(prev => ({ ...prev, [field]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: field,
          field_name: `Articles ${field.charAt(0).toUpperCase() + field.slice(1)}`,
          old_value: originalData[field],
          new_value: formData[field],
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Field "${field}" submitted for approval`);
        setPendingFields(prev => ({ ...prev, [field]: true }));
        setOriginalData(prev => ({ ...prev, [field]: formData[field] }));
        try {
          sessionStorage.removeItem('cms_page_news-updates');
          sessionStorage.removeItem('cms_page_news-and-updates');
          sessionStorage.removeItem('cms_sections_news-updates');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[NewsArticlesForm] Submit field error:', err);
      toast.error('Error submitting field: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [field]: false }));
    }
  };

  // Collect unique categories for tabs filter
  const allCategories = Array.from(new Set([
    ...DEFAULT_CATEGORIES,
    ...formData.items.map(it => it.category || it.tag).filter(Boolean)
  ]));

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Blog & Articles section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setLightboxImage(null)}
        >
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <img 
              src={lightboxImage} 
              alt="Preview" 
              style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: '12px', objectFit: 'contain' }}
            />
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-14px',
                right: '-14px',
                background: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
              }}
            >
              <X size={18} color="#0f172a" />
            </button>
          </div>
        </div>
      )}

      {/* ARTICLE EDITOR MODAL (MOUNTED DIRECTLY OR VIA PORTAL) */}
      {editorModalOpen && editingArticle && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(4px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px'
          }}
          onClick={() => setEditorModalOpen(false)}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              maxWidth: '860px',
              width: '100%',
              maxHeight: '92vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              overflow: 'hidden'
            }}
          >
            {/* Modal Header */}
            <div 
              style={{ 
                padding: '18px 24px', 
                borderBottom: '1px solid #e2e8f0', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                background: '#f8fafc'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ background: '#2563eb', color: '#fff', borderRadius: '10px', padding: '8px', display: 'flex' }}>
                  <BookOpen size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
                    {editingIndex === null ? 'Buat Berita / Artikel Baru' : `Edit Detail Berita: ${editingArticle.title || 'Artikel'}`}
                  </h3>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                    Lengkapi judul, kategori, ringkasan kartu, serta isi artikel lengkap yang tampil di halaman detail berita.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditorModalOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#64748b',
                  cursor: 'pointer',
                  padding: '6px',
                  borderRadius: '8px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Scrollable Body */}
            <div style={{ padding: '24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Row 1: Category & Date */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                    <Tag size={14} color="#2563eb" /> Kategori Berita *
                  </label>
                  <select
                    className="unified-input"
                    value={isCustomCategory ? '__CUSTOM__' : (editingArticle.category || 'Education Trends')}
                    onChange={(e) => {
                      if (e.target.value === '__CUSTOM__') {
                        setIsCustomCategory(true);
                      } else {
                        setIsCustomCategory(false);
                        setEditingArticle(prev => ({ ...prev, category: e.target.value, tag: e.target.value }));
                      }
                    }}
                    style={{ width: '100%', background: '#fff' }}
                  >
                    {DEFAULT_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="__CUSTOM__">+ Kategori Kustom Baru...</option>
                  </select>

                  {isCustomCategory && (
                    <input
                      type="text"
                      className="unified-input"
                      placeholder="Tulis nama kategori baru..."
                      value={customCategoryInput}
                      onChange={(e) => setCustomCategoryInput(e.target.value)}
                      style={{ marginTop: '8px', width: '100%' }}
                    />
                  )}
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                    <Calendar size={14} color="#2563eb" /> Tanggal Publikasi / Kegiatan *
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={editingArticle.date || ''}
                    onChange={(e) => setEditingArticle(prev => ({ ...prev, date: e.target.value }))}
                    placeholder="e.g. October 2026 atau 15 Oktober 2026"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              {/* Row 2: Title & Subtitle */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                  Judul Berita (Article Title) *
                </label>
                <input
                  type="text"
                  className="unified-input"
                  value={editingArticle.title || ''}
                  onChange={(e) => setEditingArticle(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. Siswa Permata Harapan Raih Juara 1 Kompetisi Robotik Nasional"
                  style={{ width: '100%', fontSize: '15px', fontWeight: 600 }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                    Subjudul / Topik Tambahan (Subtitle / Topic)
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={editingArticle.subtitle || ''}
                    onChange={(e) => setEditingArticle(prev => ({ ...prev, subtitle: e.target.value }))}
                    placeholder="e.g. Inovasi Sains & Pengembangan Karakter Pembelajar Mandiri"
                    style={{ width: '100%' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                    <User size={14} color="#2563eb" /> Penulis / Tim Kontributor (Author)
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={editingArticle.author || ''}
                    onChange={(e) => setEditingArticle(prev => ({ ...prev, author: e.target.value }))}
                    placeholder="e.g. Editorial Team, OSIS, atau Tim Guru Sains"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              {/* Row 3: Header Images Carousel (Up to 3 Slides) */}
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                    <ImageIcon size={15} color="#2563eb" /> Foto Header Slideshow (Maksimal 3 Foto)
                  </label>
                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                    Slide 1: Cover Utama (Wajib) • Slide 2 & 3: Header Carousel (Opsional)
                  </span>
                </div>
                <p style={{ margin: '0 0 14px 0', fontSize: '12px', color: '#64748b' }}>
                  Foto-foto ini akan membentuk slideshow otomatis pada header halaman detail berita. Jika hanya 1 foto diunggah, foto tersebut akan tampil sebagai foto cover tunggal.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                  {[0, 1, 2].map((slideIdx) => {
                    const slideImg = (editingArticle.images && editingArticle.images[slideIdx]) || (slideIdx === 0 ? editingArticle.image : '');
                    const isSlideUploading = slideUploading[slideIdx];
                    const slideProgress = slideUploadProgress[slideIdx] || 0;
                    const previewUrl = resolveImage(slideImg);
                    const isRequired = slideIdx === 0;

                    return (
                      <div 
                        key={slideIdx}
                        style={{
                          background: '#ffffff',
                          border: slideImg ? '1px solid #cbd5e1' : (isRequired ? '1px dashed #93c5fd' : '1px dashed #cbd5e1'),
                          borderRadius: '10px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        {/* Slide Title Bar */}
                        <div style={{ 
                          padding: '6px 10px', 
                          background: isRequired ? '#eff6ff' : '#f8fafc', 
                          borderBottom: '1px solid #e2e8f0', 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center' 
                        }}>
                          <span style={{ fontSize: '11.5px', fontWeight: 600, color: isRequired ? '#1d4ed8' : '#475569' }}>
                            {slideIdx === 0 ? 'Slide 1: Cover Utama *' : `Slide ${slideIdx + 1}: Header Carousel`}
                          </span>
                          {!isRequired && !slideImg && (
                            <span style={{ fontSize: '10px', color: '#94a3b8' }}>Opsional</span>
                          )}
                          {slideImg && (
                            <button
                              type="button"
                              onClick={() => handleRemoveSlideImage(slideIdx)}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}
                              title="Hapus Foto Slide"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>

                        {/* Image Preview / Dropzone */}
                        <div style={{ height: '110px', position: 'relative', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {previewUrl ? (
                            <>
                              <img 
                                src={previewUrl} 
                                alt={`Slide ${slideIdx + 1}`} 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                              <button
                                type="button"
                                onClick={() => setLightboxImage(previewUrl)}
                                style={{
                                  position: 'absolute',
                                  bottom: '6px',
                                  right: '6px',
                                  background: 'rgba(0,0,0,0.65)',
                                  border: 'none',
                                  borderRadius: '4px',
                                  color: '#fff',
                                  padding: '3px 6px',
                                  cursor: 'pointer',
                                  fontSize: '11px'
                                }}
                                title="Lihat Foto"
                              >
                                <Eye size={12} />
                              </button>
                            </>
                          ) : (
                            <label 
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                background: isRequired ? '#f8fafc' : '#ffffff',
                                cursor: isSlideUploading ? 'not-allowed' : 'pointer',
                                gap: '4px',
                                padding: '10px',
                                textAlign: 'center'
                              }}
                            >
                              <Upload size={18} color="#94a3b8" />
                              <span style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>
                                {isSlideUploading ? `Uploading ${slideProgress}%` : `Pilih Foto Slide ${slideIdx + 1}`}
                              </span>
                              <input 
                                type="file" 
                                accept="image/*" 
                                onChange={(e) => handleModalSlideImageUpload(slideIdx, e)}
                                disabled={isSlideUploading}
                                style={{ display: 'none' }}
                              />
                            </label>
                          )}

                          {isSlideUploading && (
                            <div style={{ 
                              position: 'absolute', 
                              inset: 0, 
                              background: 'rgba(15,23,42,0.85)', 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              color: '#fff', 
                              padding: '10px' 
                            }}>
                              <span style={{ fontSize: '11px', fontWeight: 600, marginBottom: '4px' }}>
                                Uploading {slideProgress}%
                              </span>
                              <div style={{ width: '80%', height: '4px', background: '#334155', borderRadius: '2px', overflow: 'hidden' }}>
                                <div style={{ width: `${slideProgress}%`, height: '100%', background: '#3b82f6', transition: 'width 0.2s ease' }} />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Re-upload button if preview exists */}
                        {previewUrl && !isSlideUploading && (
                          <div style={{ padding: '6px 8px', background: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                            <label 
                              style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                gap: '4px', 
                                fontSize: '11px', 
                                color: '#2563eb', 
                                fontWeight: 600, 
                                cursor: 'pointer',
                                margin: 0
                              }}
                            >
                              <Upload size={11} /> Ganti Foto
                              <input 
                                type="file" 
                                accept="image/*" 
                                onChange={(e) => handleModalSlideImageUpload(slideIdx, e)}
                                style={{ display: 'none' }}
                              />
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Row 4: Short Summary (Excerpt) */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                    Ringkasan Singkat / Lead Excerpt (Tampil di Card Homepage & List Berita) *
                  </label>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>Maks 2-3 kalimat ringkas</span>
                </div>
                <textarea
                  className="unified-textarea"
                  rows={3}
                  value={editingArticle.description || ''}
                  onChange={(e) => setEditingArticle(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Tuliskan ringkasan 2-3 kalimat yang menarik dan jelas untuk memancing pembaca mengklik berita..."
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>

              {/* Row 5: Full Article Body */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                    <FileText size={14} color="#2563eb" /> Isi Berita Lengkap (Tampil di Halaman Detail Berita) *
                  </label>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>Mendukung paragraf panjang</span>
                </div>
                <textarea
                  className="unified-textarea"
                  rows={8}
                  value={editingArticle.content || ''}
                  onChange={(e) => setEditingArticle(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Tuliskan seluruh isi artikel berita secara lengkap di sini. Anda dapat menulis beberapa paragraf, kutipan narasumber, dan ulasan mendalam tentang kegiatan sekolah..."
                  style={{ width: '100%', resize: 'vertical', lineHeight: '1.7' }}
                />
              </div>

              {/* Row 6: Key Milestones / Highlights */}
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                  <Sparkles size={14} color="#2563eb" /> Poin-Poin Sorotan / Key Milestones (Tampil di Halaman Detail)
                </label>
                <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#64748b' }}>
                  Tambahkan sorotan utama atau data penting terkait kegiatan ini (misal: "Juara 1 Tingkat Nasional", "Diikuti 120 siswa", dll).
                </p>

                {/* List of existing highlights */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                  {(editingArticle.highlights || []).map((hl, hIdx) => (
                    <div 
                      key={hIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px',
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        padding: '8px 12px',
                        borderRadius: '8px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        <CheckCircle2 size={15} color="#16a34a" style={{ flexShrink: 0 }} />
                        <span style={{ fontSize: '13px', color: '#334155' }}>{hl}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveHighlight(hIdx)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          padding: '3px'
                        }}
                        title="Hapus Poin"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  {(editingArticle.highlights || []).length === 0 && (
                    <p style={{ margin: 0, fontSize: '12.5px', color: '#94a3b8', fontStyle: 'italic' }}>
                      Belum ada poin sorotan yang ditambahkan.
                    </p>
                  )}
                </div>

                {/* Add highlight input */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    className="unified-input"
                    value={newHighlightText}
                    onChange={(e) => setNewHighlightText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddHighlight();
                      }
                    }}
                    placeholder="Tulis poin sorotan baru..."
                    style={{ flex: 1, fontSize: '13px' }}
                  />
                  <button
                    type="button"
                    className="unified-secondary-btn"
                    onClick={handleAddHighlight}
                    style={{ whiteSpace: 'nowrap', padding: '6px 14px', fontSize: '12.5px' }}
                  >
                    <ListPlus size={14} /> Tambah Poin
                  </button>
                </div>
              </div>

              {/* Row 7: Key Pillars & Highlights (Features - Optional) */}
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                    <Layers size={15} color="#2563eb" /> Pilar Utama & Poin Berstruktur (Key Pillars Section)
                  </label>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {(editingArticle.features || []).length > 0 && (
                      <span style={{ fontSize: '11px', background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: '10px', fontWeight: 700 }}>
                        {(editingArticle.features || []).length} Pilar Tersimpan
                      </span>
                    )}
                    <span style={{ fontSize: '11px', background: '#e2e8f0', color: '#475569', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
                      Opsional
                    </span>
                  </div>
                </div>
                <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#64748b' }}>
                  Bagian ini menampilkan kartu-kartu pilar (Key Pillars) di halaman detail berita. Jika berita Anda tidak membutuhkan poin pilar ini, Anda dapat mengosongkannya dan bagian ini tidak akan ditampilkan di halaman detail.
                </p>

                {/* List of existing features */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
                  {(editingArticle.features || []).map((feat, fIdx) => (
                    <div 
                      key={fIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: '12px',
                        background: '#ffffff',
                        border: '1.5px solid #bfdbfe',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(37,99,235,0.06)'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1 }}>
                        <div style={{ background: '#eff6ff', color: '#2563eb', padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className={`fas ${feat.icon || 'fa-star'}`} style={{ fontSize: '14px' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <h5 style={{ margin: '0 0 2px 0', fontSize: '13.5px', fontWeight: 700, color: '#0f172a' }}>
                            {feat.title}
                          </h5>
                          <p style={{ margin: 0, fontSize: '12.5px', color: '#475569', lineHeight: '1.4' }}>
                            {feat.detail}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          type="button"
                          onClick={() => handleEditFeature(fIdx)}
                          style={{
                            background: '#f1f5f9',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            color: '#334155',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '11.5px',
                            fontWeight: 600
                          }}
                          title="Edit Pilar"
                        >
                          <Edit3 size={12} /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveFeature(fIdx)}
                          style={{
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '6px',
                            color: '#ef4444',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '11.5px',
                            fontWeight: 600
                          }}
                          title="Hapus Pilar"
                        >
                          <Trash2 size={12} /> Hapus
                        </button>
                      </div>
                    </div>
                  ))}
                  {(editingArticle.features || []).length === 0 && (
                    <div style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                      <p style={{ margin: 0, fontSize: '12.5px', color: '#64748b', fontStyle: 'italic' }}>
                        Belum ada pilar yang tersimpan di daftar artikel. Gunakan formulir di bawah ini untuk menambahkan pilar baru.
                      </p>
                    </div>
                  )}
                </div>

                {/* Add new feature pillar sub-form */}
                <div style={{ background: '#ffffff', border: '1.5px solid #93c5fd', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px', boxShadow: '0 2px 6px rgba(37,99,235,0.06)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '12.5px', fontWeight: 700, color: '#1e3a8a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Plus size={15} color="#2563eb" /> Form Tambah / Edit Pilar:
                    </span>
                    {newFeatureTitle.trim() && (
                      <span style={{ fontSize: '11px', background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                        Siap disimpan
                      </span>
                    )}
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px' }}>
                    <div>
                      <label style={{ fontSize: '11.5px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '3px' }}>Pilih Ikon</label>
                      <select
                        className="unified-input"
                        value={newFeatureIcon}
                        onChange={(e) => setNewFeatureIcon(e.target.value)}
                        style={{ fontSize: '12px', padding: '7px 8px' }}
                      >
                        {FEATURE_ICON_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ fontSize: '11.5px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '3px' }}>Judul Pilar *</label>
                      <input
                        type="text"
                        className="unified-input"
                        value={newFeatureTitle}
                        onChange={(e) => setNewFeatureTitle(e.target.value)}
                        placeholder="e.g. Student Leadership, Program Inovasi"
                        style={{ fontSize: '12px', padding: '7px 8px' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: '11.5px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '3px' }}>Penjelasan Detail Pilar</label>
                    <textarea
                      className="unified-textarea"
                      rows={2}
                      value={newFeatureDetail}
                      onChange={(e) => setNewFeatureDetail(e.target.value)}
                      placeholder="Penjelasan ringkas 1-2 kalimat mengenai poin atau pilar ini..."
                      style={{ fontSize: '12px', padding: '7px 8px' }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', flexWrap: 'wrap', gap: '8px' }}>
                    <span style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic' }}>
                      {newFeatureTitle.trim() 
                        ? 'Pilar ini otomatis tersimpan saat Anda klik Simpan, atau klik tombol biru untuk menambah pilar lain.' 
                        : 'Ketik Judul Pilar dan klik tombol biru untuk memasukkan ke daftar.'}
                    </span>
                    <button
                      type="button"
                      className="unified-submit-button"
                      onClick={handleAddFeature}
                      style={{ padding: '7px 18px', fontSize: '12.5px', borderRadius: '8px', background: '#2563eb', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', border: 'none', fontWeight: 600 }}
                    >
                      <Plus size={14} /> + Tambahkan Pilar ke Daftar
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer Actions */}
            <div 
              style={{ 
                padding: '16px 24px', 
                borderTop: '1px solid #e2e8f0', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                background: '#f8fafc'
              }}
            >
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={() => setEditorModalOpen(false)}
              >
                Batal
              </button>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  className="unified-secondary-btn"
                  onClick={handleSaveModalChanges}
                  style={{ background: '#ffffff', borderColor: '#cbd5e1' }}
                >
                  <CheckCircle2 size={14} /> Terapkan ke Daftar
                </button>

                <button
                  type="button"
                  className="unified-submit-button"
                  onClick={handleSaveAndSubmitModal}
                >
                  <Save size={14} /> Simpan & Ajukan ke Approval
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Form Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <BookOpen size={22} />
        </div>
        <div>
          <h2 className="unified-title">Blog & Articles Management</h2>
          <p className="unified-subtitle">
            Kelola artikel sekolah berdasarkan kategori (Education Trends, Student Highlights, Teacher Insights). 3 artikel terbaru akan ditampilkan secara otomatis di Halaman Utama (Homepage).
          </p>
        </div>
      </div>

      {/* Integration Notice Banner */}
      <div 
        style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '12px',
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          marginBottom: '20px'
        }}
      >
        <BookOpen size={20} color="#2563eb" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <h4 style={{ margin: '0 0 4px 0', fontSize: '13.5px', fontWeight: 600, color: '#1e40af' }}>
            Sinkronisasi Otomatis dengan Homepage & Halaman Detail Berita
          </h4>
          <p style={{ margin: 0, fontSize: '12.5px', color: '#3b82f6', lineHeight: '1.5' }}>
            Anda tidak perlu menginput berita terpisah di Homepage. Sistem secara otomatis menarik 3 artikel teratas yang telah disetujui Kepala Sekolah untuk dipajang pada section berita Halaman Utama. Saat pembaca mengklik kartu berita, seluruh isi lengkap dan poin sorotan akan tampil di Halaman Detail.
          </p>
        </div>
      </div>

      {/* Field: Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker || submitting.kicker}
            placeholder="e.g. Blog / Articles"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker')}
            disabled={pendingFields.kicker || submitting.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field: Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Main Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title || submitting.title}
            placeholder="e.g. Education Trends, Student Highlights & Insights"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title')}
            disabled={pendingFields.title || submitting.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field: Description */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Description</label>
          {pendingFields.description && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            className="unified-textarea"
            rows={3}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={pendingFields.description || submitting.description}
            placeholder="Deskripsi pengantar kumpulan artikel blog..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('description')}
            disabled={pendingFields.description || submitting.description || formData.description === originalData.description}
          >
            <Save size={14} /> {submitting.description ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Articles Cards Grid with Category Filter */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Daftar Artikel & Berita ({formData.items.length})</label>
            <p className="unified-field-desc">
              Pilih kategori berita (Education Trends, Student Highlights, Teacher Insights), lengkapi ringkasan dan isi artikel secara detail.
            </p>
          </div>
          <button
            type="button"
            className="unified-submit-button"
            onClick={handleOpenAddModal}
          >
            <Plus size={14} /> Buat Berita Baru
          </button>
        </div>

        {/* Category Filter Pills */}
        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', padding: '10px 0 14px 0', borderBottom: '1px solid #f1f5f9' }}>
          <button
            type="button"
            onClick={() => setActiveCategoryFilter('ALL')}
            style={{
              padding: '6px 14px',
              borderRadius: '20px',
              border: 'none',
              background: activeCategoryFilter === 'ALL' ? '#0f172a' : '#f1f5f9',
              color: activeCategoryFilter === 'ALL' ? '#ffffff' : '#64748b',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            All Categories ({formData.items.length})
          </button>
          {allCategories.map(cat => {
            const count = formData.items.filter(it => (it.category || it.tag) === cat).length;
            const isActive = activeCategoryFilter === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategoryFilter(cat)}
                style={{
                  padding: '6px 14px',
                  borderRadius: '20px',
                  border: 'none',
                  background: isActive ? '#2563eb' : '#f1f5f9',
                  color: isActive ? '#ffffff' : '#64748b',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {cat} ({count})
              </button>
            );
          })}
        </div>

        {/* Cards Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '22px', marginTop: '18px' }}>
          {formData.items.map((item, idx) => {
            const currentCat = item.category || item.tag || 'Education Trends';
            if (activeCategoryFilter !== 'ALL' && currentCat !== activeCategoryFilter) {
              return null;
            }

            const isPending = pendingItems[`item_${idx}`];
            const isPendingDelete = pendingItems[`item_${idx}_delete`];
            const isSubmitting = submitting[`item_${idx}`];
            const isUploading = imageUploading[idx];
            const previewUrl = resolveImage(item.image, idx);
            const isSubmitDisabled = isPending || isPendingDelete || isSubmitting || !item.title || !item.image || brokenImages[idx];
            const catColors = getCategoryColor(currentCat);

            return (
              <div 
                key={item.id || idx} 
                style={{ 
                  border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0', 
                  borderRadius: '14px', 
                  background: isPending ? '#fffdfa' : '#ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Photo Dropzone / Preview */}
                <div 
                  style={{ 
                    height: '160px', 
                    background: '#0f172a',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}
                >
                  {previewUrl ? (
                    <>
                      <img 
                        src={previewUrl} 
                        alt={item.title || `Article ${idx + 1}`} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                        onClick={() => setLightboxImage(previewUrl)}
                        onError={() => setBrokenImages(prev => ({ ...prev, [idx]: true }))}
                      />
                      <button
                        type="button"
                        onClick={() => setLightboxImage(previewUrl)}
                        style={{
                          position: 'absolute',
                          bottom: '8px',
                          right: '8px',
                          background: 'rgba(0,0,0,0.65)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        title="Lihat Foto"
                      >
                        <Eye size={13} />
                      </button>
                    </>
                  ) : (
                    <label 
                      style={{
                        width: '100%',
                        height: '100%',
                        background: '#f8fafc',
                        borderBottom: '1px solid #e2e8f0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isPending || isUploading ? 'not-allowed' : 'pointer',
                        gap: '6px',
                        padding: '16px',
                        textAlign: 'center',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ background: '#e2e8f0', borderRadius: '50%', padding: '10px', display: 'flex' }}>
                        <Upload size={20} color="#64748b" />
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                        {isUploading ? `Mengupload (${uploadProgress[idx] || 0}%)...` : 'Upload Foto Artikel'}
                      </span>
                      {isUploading && (
                        <div style={{ width: '80%', height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden', marginTop: '6px' }}>
                          <div style={{ width: `${uploadProgress[idx] || 0}%`, height: '100%', background: '#3b82f6', transition: 'width 0.2s ease' }} />
                        </div>
                      )}
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>JPG, PNG, WebP (Maks 5MB)</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={(e) => handleCardImageUpload(idx, e)}
                        disabled={isPending || isUploading}
                        style={{ display: 'none' }}
                      />
                    </label>
                  )}
                </div>

                {/* Card Body */}
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px', flex: 1, justifyContent: 'space-between' }}>
                  <div>
                    {/* Header Row: Category Badge & Delete Button */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span 
                          style={{ 
                            fontSize: '11px', 
                            padding: '3px 8px', 
                            borderRadius: '12px', 
                            fontWeight: 600, 
                            background: catColors.bg,
                            color: catColors.text,
                            border: `1px solid ${catColors.border}`
                          }}
                        >
                          {currentCat}
                        </span>
                        {isPendingDelete ? (
                          <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
                            <Clock size={11} /> Pending Delete
                          </span>
                        ) : isPending ? (
                          <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                            <Clock size={11} /> Pending Review
                          </span>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(idx)}
                        disabled={isPending || isPendingDelete || formData.items.length <= 1}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: isPending || isPendingDelete || formData.items.length <= 1 ? '#cbd5e1' : '#ef4444', 
                          cursor: isPending || isPendingDelete || formData.items.length <= 1 ? 'not-allowed' : 'pointer', 
                          padding: '4px' 
                        }}
                        title="Hapus Artikel"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>

                    {/* Title */}
                    <h4 style={{ margin: '0 0 6px 0', fontSize: '15px', fontWeight: 700, color: '#0f172a', lineHeight: '1.3' }}>
                      {item.title || 'Artikel Tanpa Judul'}
                    </h4>

                    {/* Meta: Date & Author */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '11.5px', color: '#64748b', marginBottom: '8px' }}>
                      <span><Calendar size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} />{item.date || 'October 2026'}</span>
                      {item.author && <span>• <User size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} />{item.author}</span>}
                    </div>

                    {/* Description Excerpt */}
                    <p style={{ margin: '0 0 10px 0', fontSize: '12.5px', color: '#475569', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {item.description || item.content || 'Belum ada ringkasan artikel.'}
                    </p>

                    {/* Highlights, Features & Slides Badges */}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                      {(item.highlights && item.highlights.length > 0) && (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#16a34a', background: '#f0fdf4', padding: '2px 8px', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                          <Sparkles size={11} /> {item.highlights.length} Poin Sorotan
                        </div>
                      )}
                      {(item.features && item.features.length > 0) && (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#2563eb', background: '#eff6ff', padding: '2px 8px', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
                          <Layers size={11} /> {item.features.length} Pilar Utama
                        </div>
                      )}
                      {(item.images && item.images.length > 1) && (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#7c3aed', background: '#f5f3ff', padding: '2px 8px', borderRadius: '6px', border: '1px solid #ddd6fe' }}>
                          <ImageIcon size={11} /> {item.images.length} Header Slides
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions: Edit Full Article & Submit */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      onClick={() => handleOpenEditModal(idx)}
                      disabled={isPendingDelete}
                      style={{ 
                        justifyContent: 'center', 
                        fontSize: '12.5px', 
                        padding: '8px 12px',
                        cursor: isPendingDelete ? 'not-allowed' : 'pointer',
                        opacity: isPendingDelete ? 0.6 : 1
                      }}
                    >
                      <Edit3 size={13} /> Edit Detail Berita Lengkap
                    </button>

                    <button
                      type="button"
                      className="unified-submit-button"
                      style={{ width: '100%', justifyContent: 'center', padding: '8px 14px', fontSize: '13px' }}
                      onClick={() => handleSubmitItem(idx)}
                      disabled={isSubmitDisabled}
                    >
                      <Save size={14} /> {isSubmitting ? 'Menyimpan...' : 'Save & Submit for Approval'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
